import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { PerfisService } from '../services/perfisService';

export const usePermissoes = (modulo: string, permissao: string) => {
  const { user } = useAuth();
  const [temPermissao, setTemPermissao] = useState<boolean>(false);
  const [carregando, setCarregando] = useState<boolean>(true);

  useEffect(() => {
    const verificarPermissao = async () => {
      if (!user) {
        setTemPermissao(false);
        setCarregando(false);
        return;
      }

      try {
        // Usar perfil_id se disponível, caso contrário usar perfil (nome)
        const perfilId = (user as any).perfil_id || user.perfil;
        const { temPermissao: temAcesso } = await PerfisService.verificarPermissao(
          perfilId,
          modulo as any,
          permissao as any
        );
        setTemPermissao(temAcesso);
      } catch (error) {
        console.error('Erro ao verificar permissão:', error);
        // Fallback: se erro na verificação, negar acesso por segurança
        setTemPermissao(false);
      } finally {
        setCarregando(false);
      }
    };

    verificarPermissao();
  }, [user, modulo, permissao]);

  return { temPermissao, carregando };
};

export const useVerificarAcessoAba = (aba: string) => {
  const { user } = useAuth();
  const [temAcesso, setTemAcesso] = useState<boolean>(false);
  const [carregando, setCarregando] = useState<boolean>(true);

  useEffect(() => {
    const verificarAcesso = async () => {
      if (!user) {
        setTemAcesso(false);
        setCarregando(false);
        return;
      }

      const perfilUser = (user as any).perfil_id || user.perfil;

      // Restrição hardcoded: visitante só pode acessar Agendar Visitas
      if (perfilUser === 'visitante') {
        setTemAcesso(aba === 'Agendar Visitas');
        setCarregando(false);
        return;
      }

      try {
        // Usar perfil_id se disponível, caso contrário usar perfil (nome)
        const perfilId = perfilUser;
        
        // Mapear abas para módulos e permissões correspondentes no sistema de permissões granulares
        const mapaAbasParaPermissoes: Record<string, { modulo: string; permissao: string }> = {
          'Início': { modulo: 'inicio', permissao: 'ver' },
          'Dashboard': { modulo: 'inicio', permissao: 'ver' },
          'Agendamentos': { modulo: 'gestao_agendamento', permissao: 'ver' },
          'Solicitações': { modulo: 'solicitacao_cadastro', permissao: 'ver' },
          'Visualizar Solicitação': { modulo: 'solicitacao_cadastro', permissao: 'ver' },
          'Cargas': { modulo: 'cargas', permissao: 'ver' },
          'Gestão de Agendamento': { modulo: 'gestao_agendamento', permissao: 'ver' },
          'Gestão de Pedidos': { modulo: 'gestao_pedidos', permissao: 'ver' },
          'Gestão de Pedidos de Compra': { modulo: 'gestao_pedidos', permissao: 'ver' },
          'BI Analítico': { modulo: 'bi_analitico', permissao: 'ver' },
          'Área do RCA': { modulo: 'area_rca', permissao: 'ver' },
          'Monitoramento de Sistema': { modulo: 'logs', permissao: 'ver' },
          'Usuários': { modulo: 'usuarios', permissao: 'ver' },
          'Configurações': { modulo: 'configuracoes', permissao: 'ver' },
          'Filiais': { modulo: 'filiais', permissao: 'ver' },
          'Agendar Visitas': { modulo: 'visitas', permissao: 'ver_agendar_visitas' },
          'Gestão de Visitas': { modulo: 'visitas', permissao: 'ver_gestao_visitas' },
          'Fale com o desenvolvedor': { modulo: 'inicio', permissao: 'ver' }
        };

        const mapeamento = mapaAbasParaPermissoes[aba];

        if (!mapeamento) {
          console.error('Aba não mapeada para módulo/permissão:', aba);
          setTemAcesso(false);
          setCarregando(false);
          return;
        }

        // Verificar permissão de acesso ao módulo no sistema granular
        const { temPermissao: temAcessoAba } = await PerfisService.verificarPermissao(
          perfilId,
          mapeamento.modulo as any,
          mapeamento.permissao as any
        );
        
        setTemAcesso(temAcessoAba);
      } catch (error) {
        console.error('Erro ao verificar acesso à aba:', error);
        // Fallback: usar sistema legado de mapeamento hardcoded
        const abasPorPerfil: Record<string, string[]> = {
          'admin': [
            'Início', 'Dashboard', 'Agendamentos', 'Cargas', 'Solicitações',
            'Visualizar Solicitação', 'Gestão de Pedidos', 'Gestão de Agendamento',
            'BI Analítico', 'Área do RCA', 'Monitoramento de Sistema',
            'Usuários', 'Configurações', 'Filiais', 'Agendar Visitas', 'Gestão de Visitas'
          ],
          'gerente': [
            'Início', 'Dashboard', 'Agendamentos', 'Cargas', 'Solicitações',
            'Visualizar Solicitação', 'Gestão de Pedidos', 'Gestão de Agendamento',
            'BI Analítico', 'Área do RCA', 'Agendar Visitas', 'Gestão de Visitas'
          ],
          'operador': [
            'Início', 'Dashboard', 'Agendamentos', 'Cargas', 'Solicitações',
            'Visualizar Solicitação', 'Gestão de Agendamento', 'Agendar Visitas', 'Gestão de Visitas'
          ],
          'gestor': [
            'Início', 'Dashboard', 'Agendamentos', 'Cargas', 'Solicitações',
            'Visualizar Solicitação', 'Gestão de Pedidos', 'Gestão de Agendamento',
            'BI Analítico', 'Área do RCA', 'Agendar Visitas', 'Gestão de Visitas'
          ],
          'gestores': [
            'Início', 'Dashboard', 'Agendamentos', 'Cargas', 'Solicitações',
            'Visualizar Solicitação', 'Gestão de Pedidos', 'Gestão de Agendamento',
            'BI Analítico', 'Área do RCA', 'Agendar Visitas', 'Gestão de Visitas'
          ],
          'fornecedor': [
            'Início', 'Agendar Visitas'
          ],
          'visitante': [
            'Agendar Visitas'
          ]
        };

        const perfil = user.perfil;
        const abasPermitidas = abasPorPerfil[perfil] || [];
        
        // Verificar se a aba está na lista de abas permitidas para o perfil
        const acesso = perfil === 'admin' || abasPermitidas.includes(aba);
        setTemAcesso(acesso);
      } finally {
        setCarregando(false);
      }
    };

    verificarAcesso();
  }, [user, aba]);

  return { temAcesso, carregando };
};
